// Corporate Performance — เวลาตอบ inbox เฉลี่ยของแต่ละเพจ แยกตามโปรเจกต์
//
// ตัวเลขมาจาก Inbox Center ทั้งหมด (perPage ของ /api/analytics) ไม่ได้คิดใหม่ที่นี่
// เพื่อให้ตรงกับที่หน้า Analytics ของเขารายงาน ไม่มีสองสำนักคิดคนละแบบ
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Topbar } from "@features/navigation/components/topbar";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { RangePills } from "@features/overview/components/range-pills";
import { fmtDuration, fmtInt } from "@shared/lib/format";
import { cn } from "@shared/lib/utils";
import {
  corporateKeys,
  fetchProjectPerformance,
  fetchProjects,
  type InboxProject,
} from "../api/corporate-api";

/** โปรเจกต์ที่เปิดให้เป็นค่าเริ่มต้น — ทีมที่ดูหน้านี้บ่อยที่สุด */
const DEFAULT_PROJECT = "creative content team";

const RANGES = [
  { v: 7, label: "7 วัน" },
  { v: 15, label: "15 วัน" },
  { v: 30, label: "30 วัน" },
];

/** ช้ากว่านี้ถือว่าต้องรีบแก้ — เกณฑ์เดียวกับ bubble ในหน้า Overview (1 วัน / 3 วัน) */
const DAY = 86400000;
const tone = (ms: number | null | undefined) =>
  ms == null ? "" : ms < DAY ? "ok" : ms < 3 * DAY ? "warn" : "bad";

export function CorporatePerformancePage() {
  const hasToken = !!wazzupToken();
  const [days, setDays] = useState(7);
  // เก็บแค่ "ที่ผู้ใช้เลือกเอง" — ค่าเริ่มต้นคำนวณจากรายชื่อที่โหลดมา ไม่ต้อง setState ใน effect
  const [pickedId, setPickedId] = useState<string | null>(null);

  const projects = useQuery({
    queryKey: corporateKeys.projects,
    queryFn: fetchProjects,
    enabled: hasToken,
    retry: false,
  });

  const list = projects.data ?? [];
  const defaultId =
    list.find((p) => p.name.trim().toLowerCase() === DEFAULT_PROJECT)?.id ?? list[0]?.id ?? null;
  const projectId = pickedId ?? defaultId;
  const current = list.find((p) => p.id === projectId) ?? null;

  const perf = useQuery({
    queryKey: corporateKeys.perf(projectId ?? "", days),
    queryFn: () => fetchProjectPerformance(projectId!, days),
    enabled: hasToken && !!projectId,
  });

  return (
    <>
      <Topbar active="corporate" />
      <main className="page">
        <div className="container">
          <div className="page-head">
            <div>
              <h1>Corporate Performance</h1>
              <p className="subtitle">
                เวลาตอบ inbox เฉลี่ยของแต่ละเพจ — เลือกกลุ่มเพจได้จากโปรเจกต์ใน Inbox Center
              </p>
            </div>
            <RangePills options={RANGES} value={days} onPick={setDays} />
          </div>

          {!hasToken ? (
            <div className="panel empty-state">
              <h3>ต้องเข้าสู่ระบบ Wazzup ก่อน</h3>
              <p className="muted">ตัวเลขทั้งหมดมาจาก Inbox Center ซึ่งต้องยืนยันตัวตนก่อนดึง</p>
              <a className="btn-primary inline-btn" href="/setting/staff">
                ไปหน้าเข้าสู่ระบบ
              </a>
            </div>
          ) : (
            <>
              <ProjectPicker
                projects={list}
                loading={projects.isPending}
                failed={projects.isError}
                value={projectId}
                onPick={setPickedId}
              />

              {projects.isError ? (
                <div className="panel">
                  <p className="muted">ดึงรายชื่อโปรเจกต์จาก Inbox Center ไม่สำเร็จ</p>
                </div>
              ) : perf.isPending && projectId ? (
                <div className="ov-loading">กำลังโหลด…</div>
              ) : perf.isError ? (
                <div className="panel">
                  <p className="muted">ดึงข้อมูลการตอบ inbox ไม่สำเร็จ</p>
                </div>
              ) : current ? (
                <PerformanceBoxes project={current} days={days} data={perf.data} />
              ) : (
                <div className="panel">
                  <p className="muted">
                    ยังไม่มีโปรเจกต์ใน Inbox Center — สร้างกลุ่มเพจที่ระบบนั้นก่อน
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}

function ProjectPicker({
  projects,
  loading,
  failed,
  value,
  onPick,
}: {
  projects: InboxProject[];
  loading: boolean;
  failed: boolean;
  value: string | null;
  onPick: (id: string) => void;
}) {
  return (
    <div className="toolbar">
      <label className="camp-select-wrap">
        <span className="muted camp-select-lbl">Project</span>
        <select
          className="camp-select"
          value={value ?? ""}
          disabled={loading || failed || !projects.length}
          onChange={(e) => onPick(e.target.value)}
        >
          {loading ? (
            <option value="">กำลังโหลดโปรเจกต์…</option>
          ) : (
            projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.pageIds.length} เพจ)
              </option>
            ))
          )}
        </select>
      </label>
    </div>
  );
}

function PerformanceBoxes({
  project,
  days,
  data,
}: {
  project: InboxProject;
  days: number;
  data: import("@features/overview/api/upstream-types").InboxAnalytics | undefined;
}) {
  const resp = data?.response ?? {};
  const totals = data?.totals ?? {};
  // เรียงช้าที่สุดขึ้นก่อน — หน้านี้มีไว้หาเพจที่ต้องเข้าไปช่วย
  const pages = [...(data?.perPage ?? [])].sort(
    (a, b) => (b.avgHumanMs ?? -1) - (a.avgHumanMs ?? -1),
  );

  return (
    <>
      <div className="section-label">
        {project.name} · ย้อนหลัง {days} วัน
      </div>

      <div className="ov-kpis">
        <div className="ov-kpi">
          <div className="ov-kpi-label">เวลาตอบเฉลี่ยรวมทุกเพจ</div>
          <div className="ov-kpi-value">{fmtDuration(resp.avgHumanMs)}</div>
          <div className="ov-kpi-sub muted">
            {resp.humanCount ? `จาก ${fmtInt(resp.humanCount)} ครั้ง` : "ยังไม่มีการตอบ"}
          </div>
        </div>
        <div className="ov-kpi">
          <div className="ov-kpi-label">เพจในโปรเจกต์</div>
          <div className="ov-kpi-value">{fmtInt(project.pageIds.length)}</div>
          <div className="ov-kpi-sub muted">{pages.length} เพจมีข้อความในช่วงนี้</div>
        </div>
        <div className="ov-kpi">
          <div className="ov-kpi-label">ข้อความลูกค้า</div>
          <div className="ov-kpi-value">{fmtInt(totals.periodIn)}</div>
          <div className="ov-kpi-sub muted">{fmtInt(totals.activeRooms)} ห้องที่เคลื่อนไหว</div>
        </div>
      </div>

      <div className="section-label">เวลาตอบเฉลี่ยต่อเพจ</div>
      {!pages.length ? (
        <div className="panel">
          <p className="muted">ไม่มีเพจไหนมีข้อความในช่วง {days} วันนี้</p>
        </div>
      ) : (
        <div className="ov-kpis">
          {pages.map((p) => (
            <div
              className={cn("ov-kpi", "cp-page", tone(p.avgHumanMs) === "bad" && "is-bad")}
              key={p.pageId}
            >
              <div className="ov-kpi-label" title={p.pageName || p.pageId}>
                {p.pageName || p.pageId}
              </div>
              <div className="ov-kpi-value">{fmtDuration(p.avgHumanMs)}</div>
              <div className="ov-kpi-sub muted">
                {fmtInt(p.periodIn)} ข้อความ
                {p.waiting ? ` · ค้าง ${fmtInt(p.waiting)}` : ""}
              </div>
              {/* ห้องที่ค้างเกิน 24 ชม. ต้องเห็นทันที ไม่ต้องเทียบตัวเลขเอง */}
              {p.over24h ? (
                <div className="cp-flag bad">⛔ ค้างเกิน 24 ชม. {fmtInt(p.over24h)} ห้อง</div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
